import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Home as HomeIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import Layout from '../components/Layout';
import { messagesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const MessagesPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchConversations();
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages();
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await messagesApi.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await messagesApi.getMessages(conversationId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const currentConv = conversations.find(c => c.conversation_id === conversationId);
    const recipient = currentConv?.participants.find(p => p.user_id !== user?.user_id);

    if (!recipient) {
      toast.error('Could not find recipient');
      return;
    }

    setSending(true);
    try {
      await messagesApi.send({
        recipient_id: recipient.user_id,
        property_id: currentConv?.property_id,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages();
      fetchConversations();
    } catch (error) {
      // Handle contact info moderation blocking
      const errorData = error.response?.data?.detail;
      if (errorData?.code === 'CONTACT_INFO_BLOCKED') {
        toast.error(errorData.message, { duration: 6000 });
      } else if (typeof errorData === 'object' && errorData?.message) {
        toast.error(errorData.message, { duration: 6000 });
      } else {
        toast.error(typeof errorData === 'string' ? errorData : 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout showFooter={false}>
        <div className="h-[calc(100vh-64px)] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  const currentConversation = conversations.find(c => c.conversation_id === conversationId);
  const otherParticipant = currentConversation?.participants.find(p => p.user_id !== user?.user_id);

  return (
    <Layout showFooter={false}>
      <div className="h-[calc(100vh-64px)] flex" data-testid="messages-page">
        {/* Conversations List */}
        <div className={`w-full md:w-80 border-r border-slate-200 flex flex-col ${conversationId ? 'hidden md:flex' : ''}`}>
          <div className="p-4 border-b border-slate-200">
            <h1 className="font-heading text-xl font-bold text-slate-900">Messages</h1>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <p>No conversations yet</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const other = conv.participants.find(p => p.user_id !== user?.user_id);
                const isActive = conv.conversation_id === conversationId;
                return (
                  <Link
                    key={conv.conversation_id}
                    to={`/messages/${conv.conversation_id}`}
                    className={`block p-4 border-b border-slate-100 hover:bg-soft-gray transition-colors ${
                      isActive ? 'bg-soft-gray' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={other?.picture} />
                        <AvatarFallback className="bg-trust-blue text-white">
                          {other?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-slate-900 truncate">{other?.name}</h4>
                          {conv.unread_count > 0 && (
                            <span className="w-5 h-5 bg-coral text-white text-xs rounded-full flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        {conv.property_title && (
                          <p className="text-xs text-trust-blue truncate">{conv.property_title}</p>
                        )}
                        {conv.last_message && (
                          <p className="text-sm text-slate-500 truncate">{conv.last_message}</p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        {conversationId ? (
          <div className="flex-1 flex flex-col">
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-200 flex items-center gap-4">
              <Link to="/messages" className="md:hidden">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <Avatar>
                <AvatarImage src={otherParticipant?.picture} />
                <AvatarFallback className="bg-trust-blue text-white">
                  {otherParticipant?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h4 className="font-medium text-slate-900">{otherParticipant?.name}</h4>
                {currentConversation?.property_title && (
                  <Link 
                    to={`/property/${currentConversation.property_id}`}
                    className="text-sm text-trust-blue hover:underline flex items-center gap-1"
                  >
                    <HomeIcon className="w-3 h-3" />
                    {currentConversation.property_title}
                  </Link>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => {
                const isMe = msg.sender_id === user?.user_id;
                return (
                  <div
                    key={msg.message_id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${isMe ? 'order-2' : ''}`}>
                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          isMe 
                            ? 'bg-trust-blue text-white rounded-br-sm' 
                            : 'bg-soft-gray text-slate-900 rounded-bl-sm'
                        }`}
                      >
                        <p>{msg.content}</p>
                      </div>
                      <p className={`text-xs text-slate-400 mt-1 ${isMe ? 'text-right' : ''}`}>
                        {format(new Date(msg.created_at), 'h:mm a')}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="p-4 border-t border-slate-200">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button 
                  type="submit" 
                  disabled={sending || !newMessage.trim()}
                  className="bg-trust-blue hover:bg-trust-blue-hover"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center text-slate-500">
            Select a conversation to start messaging
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MessagesPage;
